import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Divider,
  Alert,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import api from '../config/api';
import { toast } from 'react-toastify';

// Lista de eventos disponíveis no Baileys
const EVENTOS_DISPONIVEIS = [
  'connection.update',
  'creds.update',
  'messages.upsert',
  'messages.update',
  'messages.delete',
  'presence.update',
  'groups.update',
  'group-participants.update',
  'call',
  'contacts.upsert',
  'contacts.update'
];

function Webhook() {
  const [loading, setLoading] = useState(true);
  const [instancias, setInstancias] = useState([]);
  const [webhooks, setWebhooks] = useState({});
  const [saving, setSaving] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Buscar instâncias
      const instanciasResponse = await api.get('/instancias');
      setInstancias(instanciasResponse.data);
      
      // Buscar webhooks para cada instância
      const webhooksData = {};
      for (const instancia of instanciasResponse.data) {
        try {
          const webhookResponse = await api.get(`/webhook/${instancia.idInstancia}`);
          webhooksData[instancia.idInstancia] = webhookResponse.data;
        } catch (error) {
          // Se não houver webhook (404 é esperado), usar valores padrão
          // Não mostrar erro para 404, pois é normal não ter webhook configurado
          if (error.response && error.response.status === 404) {
            webhooksData[instancia.idInstancia] = {
              idWebhook: null,
              idInstancia: instancia.idInstancia,
              url: '',
              eventos: []
            };
          } else {
            // Para outros erros, logar mas ainda usar valores padrão
            console.warn(`Erro ao buscar webhook para instância ${instancia.idInstancia}:`, error);
            webhooksData[instancia.idInstancia] = {
              idWebhook: null,
              idInstancia: instancia.idInstancia,
              url: '',
              eventos: []
            };
          }
        }
      }
      setWebhooks(webhooksData);
    } catch (error) {
      console.error('Erro ao buscar dados:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (idInstancia) => {
    setSaving({ ...saving, [idInstancia]: true });
    try {
      const webhook = webhooks[idInstancia];
      await api.post(`/webhook/${idInstancia}`, {
        url: webhook.url,
        eventos: webhook.eventos
      });
      toast.success('Webhook salvo com sucesso');
      fetchData();
    } catch (error) {
      console.error('Erro ao salvar webhook:', error);
      toast.error('Erro ao salvar webhook');
    } finally {
      setSaving({ ...saving, [idInstancia]: false });
    }
  };

  const handleDelete = async (idInstancia) => {
    if (!window.confirm('Tem certeza que deseja deletar o webhook desta instância?')) {
      return;
    }
    
    try {
      await api.delete(`/webhook/${idInstancia}`);
      toast.success('Webhook deletado com sucesso');
      fetchData();
    } catch (error) {
      console.error('Erro ao deletar webhook:', error);
      toast.error('Erro ao deletar webhook');
    }
  };

  const handleEventToggle = (idInstancia, evento) => {
    setWebhooks((prev) => ({
      ...prev,
      [idInstancia]: {
        ...prev[idInstancia],
        eventos: prev[idInstancia].eventos.includes(evento)
          ? prev[idInstancia].eventos.filter((e) => e !== evento)
          : [...prev[idInstancia].eventos, evento]
      }
    }));
  };

  const handleUrlChange = (idInstancia, url) => {
    setWebhooks((prev) => ({
      ...prev,
      [idInstancia]: {
        ...prev[idInstancia],
        url: url
      }
    }));
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Configuração de Webhooks por Instância
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        Configure webhooks individuais para cada instância. Cada instância pode ter seu próprio webhook com eventos específicos.
      </Alert>

      {instancias.length === 0 ? (
        <Alert severity="warning">
          Nenhuma instância encontrada. Crie uma instância primeiro.
        </Alert>
      ) : (
        instancias.map((instancia) => {
          const webhook = webhooks[instancia.idInstancia] || {
            idWebhook: null,
            idInstancia: instancia.idInstancia,
            url: '',
            eventos: []
          };

          return (
            <Accordion key={instancia.idInstancia} sx={{ mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center', pr: 2 }}>
                  <Typography variant="h6">{instancia.nome}</Typography>
                  {webhook.idWebhook && (
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(instancia.idInstancia);
                      }}
                      color="error"
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ mb: 3 }}>
                  <TextField
                    fullWidth
                    label="URL do Webhook"
                    value={webhook.url}
                    onChange={(e) => handleUrlChange(instancia.idInstancia, e.target.value)}
                    margin="normal"
                    helperText="URL que receberá as notificações de eventos desta instância"
                  />
                </Box>

                <Typography variant="subtitle1" gutterBottom>
                  Eventos
                </Typography>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Selecione os eventos que deseja receber no webhook para esta instância
                </Alert>
                <FormGroup>
                  {EVENTOS_DISPONIVEIS.map((evento) => (
                    <FormControlLabel
                      key={evento}
                      control={
                        <Checkbox
                          checked={webhook.eventos.includes(evento)}
                          onChange={() => handleEventToggle(instancia.idInstancia, evento)}
                        />
                      }
                      label={evento}
                    />
                  ))}
                </FormGroup>

                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    onClick={() => handleSave(instancia.idInstancia)}
                    disabled={saving[instancia.idInstancia]}
                  >
                    {saving[instancia.idInstancia] ? 'Salvando...' : 'Salvar Webhook'}
                  </Button>
                </Box>
              </AccordionDetails>
            </Accordion>
          );
        })
      )}
    </Box>
  );
}

export default Webhook; 